import React, { useState } from "react";
import { Box } from "@mui/material";
import { CalendarMonth, AccessTimeRounded } from "@mui/icons-material";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import moment from "moment";
import { useSelector } from "react-redux";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestStatusTimeline } from "components/DesignSystem/RequestStatusTimeline";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import RequestDetailsSection from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { normalizeApprovalPersonFields } from "pages/TeamZone/Approve/displayNameUtils";

const formatOTDateTime = (value) => {
  if (!value) return "";
  const parsed = moment(
    value,
    ["DD-MM-YYYY, HH:mm", "DD-MM-YYYY, hh:mm A", moment.ISO_8601],
    true,
  );
  return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : value;
};

const formatHours = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const text = String(value);
  return /hrs?$/i.test(text.trim()) ? text : `${text} Hrs`;
};

export const ViewOTPostApproval = ({
  open = true,
  selectedData,
  onClose,
  onUpdated,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const requestId = selectedData?.id || selectedData?.approval_id || selectedData?.ot_id;
  const canCancel = selectedData?.status === "Pending" && Boolean(requestId);
  const displayData = normalizeApprovalPersonFields({
    ...selectedData,
    employee_name:
      selectedData?.employee_name ||
      selectedData?.user_name ||
      selectedData?.username ||
      userDetails?.first_name,
    employee_id: selectedData?.employee_id || selectedData?.emp_id || userDetails?.employee_id,
  });
  const employeeName = displayData.employee_name || "N/A";
  const employeeId =
    selectedData?.employee_id ||
    selectedData?.emp_id ||
    userDetails?.employee_id ||
    "-";
  const department =
    selectedData?.department_name ||
    selectedData?.department ||
    userDetails?.department_name ||
    "-";
  const designation =
    selectedData?.designation_name ||
    selectedData?.designation ||
    userDetails?.designation ||
    "-";
  const statusFlowData = {
    ...selectedData,
    applied_by: displayData.applied_by || selectedData?.createdby || employeeName,
    applied_on: formatOTDateTime(
      selectedData?.applied_on ||
        selectedData?.applied_date ||
        selectedData?.created_at ||
        selectedData?.created_date,
    ),
    approved_by: displayData.approved_by,
    approved_at: formatOTDateTime(
      selectedData?.approved_at || selectedData?.approved_on,
    ),
    rejected_by: displayData.rejected_by,
    rejected_at: formatOTDateTime(
      selectedData?.rejected_at || selectedData?.rejected_on,
    ),
    cancelled_by: displayData.cancelled_by,
    cancelled_at: formatOTDateTime(
      selectedData?.cancelled_at ||
        selectedData?.cancelled_on ||
        selectedData?.canceled_at ||
        selectedData?.canceled_on,
    ),
    cancelled_reason:
      selectedData?.cancelled_reason ||
      selectedData?.cancel_reason ||
      selectedData?.canceled_reason,
  };

  const handleCancelRequest = async () => {
    setCancelling(true);
    const response = await customAPI(
      `${domain}My_Zone/Apply/Over_time/ot-post-approval/?company_id=${requestCompanyId}`,
      "PUT",
      {
        id: requestId,
        status: "Cancelled",
      },
    );
    setCancelling(false);
    setShowCancelDialog(false);
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message || "Request cancelled");
      onUpdated?.();
      onClose?.();
    } else {
      showToast("error", response?.message);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Overtime Request"
      subtitle="Request details and status"
      footer={
        canCancel ? (
          <RequestCancelButton onClick={() => setShowCancelDialog(true)} />
        ) : null
      }
    >
      <ConfirmationDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelRequest}
        title="Cancel Request"
        message="Are you sure you want to cancel this request?"
        confirmText="Confirm"
        cancelText="Cancel"
        disableAction={cancelling}
      />
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <RequestDetailHeader
          userDetails={{
            image_url: selectedData?.image_url || userDetails?.image_url,
            employee_id: employeeId,
            user_name: employeeName,
            department_name: department,
            designation,
          }}
          status={selectedData?.status}
          colorCode={selectedData?.color_code}
          variant="employeeDetailsCard"
        />
        <RequestDetailsSection
          fields={[
            { icon: CalendarMonth, label: "Date", value: selectedData?.date || "-", accent: detailAccentPalette.indigo },
            { icon: AccessTimeRounded, label: "Applied On", value: statusFlowData.applied_on || "-", accent: detailAccentPalette.blue },
            { icon: AccessTimeRounded, label: "Worked Hours", value: formatHours(selectedData?.worked_hours), helper: `Basis: ${selectedData?.basis || "-"}`, accent: detailAccentPalette.pink },
            { icon: AccessTimeRounded, label: "Worked / Approved Qty", value: `${selectedData?.worked_qty || "-"} / ${selectedData?.approved_qty || "-"}`, accent: detailAccentPalette.blue },
            { icon: PaidOutlinedIcon, label: "Payout Policy", value: selectedData?.payout_policy || "-", accent: detailAccentPalette.orange, multiline: true },
          ]}
        />

        <RequestStatusTimeline endDate={selectedData?.date} data={statusFlowData} hidePendingStep />

      </Box>
    </GlobalSidePanel>
  );
};

export default ViewOTPostApproval;
